import React, { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

const DEFAULT_SETTINGS = {
  enabled: false,
  provider: "openai",
  model: "gpt-4.1-mini",
  allowed_roles: ["admin"],
  system_prompt: "Eres un asistente especializado en gestion de almacen de moda.",
  max_response_length: 400,
  max_tokens_per_request: 1200,
  enable_audit_logging: true,
  temperature: 0.2,
};

export function AIChatSettings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("app_settings")
        .select("id, ai_chat_settings")
        .limit(50);

      if (error) throw error;

      const settingsRow = (data || []).find((row) => row.ai_chat_settings) || data?.[0];

      if (settingsRow?.ai_chat_settings) {
        setSettings({
          ...DEFAULT_SETTINGS,
          ...settingsRow.ai_chat_settings,
          allowed_roles: Array.isArray(settingsRow.ai_chat_settings.allowed_roles)
            ? settingsRow.ai_chat_settings.allowed_roles
            : DEFAULT_SETTINGS.allowed_roles,
        });
      }
    } catch (error) {
      console.error("Error fetching ai chat settings:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los ajustes del chat IA.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { data: existingRows, error: fetchError } = await supabase
        .from("app_settings")
        .select("id, ai_chat_settings")
        .limit(50);

      if (fetchError) throw fetchError;

      const targetRow = (existingRows || []).find((row) => row.ai_chat_settings) || existingRows?.[0];

      if (targetRow?.id) {
        const { error: updateError } = await supabase
          .from("app_settings")
          .update({
            ai_chat_settings: settings,
          })
          .eq("id", targetRow.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from("app_settings")
          .insert({
            ai_chat_settings: settings,
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Éxito",
        description: "Configuración del chat IA guardada correctamente.",
      });
    } catch (error) {
      console.error("Error saving ai chat settings:", error);
      toast({
        title: "Error",
        description: "No se pudieron guardar los ajustes del chat IA.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Cargando configuración del chat IA...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Configuración del Chat IA</CardTitle>
          <CardDescription>
            Controla activación, proveedor, modelo y comportamiento del asistente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center space-x-2">
            <Switch
              id="ai-chat-enabled"
              checked={settings.enabled}
              onCheckedChange={(checked) => updateSetting("enabled", checked)}
            />
            <Label htmlFor="ai-chat-enabled">Habilitar chat IA</Label>
          </div>

          <div className="space-y-2">
            <Label htmlFor="provider">Proveedor</Label>
            <Select value={settings.provider} onValueChange={(value) => updateSetting("provider", value)}>
              <SelectTrigger id="provider">
                <SelectValue placeholder="Selecciona proveedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="openai">OpenAI</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="model">Modelo</Label>
            <Input
              id="model"
              value={settings.model}
              onChange={(e) => updateSetting("model", e.target.value)}
              placeholder="gpt-4.1-mini"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="system_prompt">Prompt del sistema</Label>
            <Textarea
              id="system_prompt"
              value={settings.system_prompt}
              onChange={(e) => updateSetting("system_prompt", e.target.value)}
              rows={6}
              className="font-mono text-sm"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="max_response_length">Longitud máxima de respuesta</Label>
              <Input
                id="max_response_length"
                type="number"
                min="50"
                max="2000"
                value={settings.max_response_length}
                onChange={(e) => updateSetting("max_response_length", parseInt(e.target.value, 10) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="max_tokens_per_request">Tokens máximos por petición</Label>
              <Input
                id="max_tokens_per_request"
                type="number"
                min="100"
                max="8000"
                value={settings.max_tokens_per_request}
                onChange={(e) => updateSetting("max_tokens_per_request", parseInt(e.target.value, 10) || 0)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="temperature">Temperature</Label>
            <Input
              id="temperature"
              type="number"
              min="0"
              max="2"
              step="0.1"
              value={settings.temperature}
              onChange={(e) => updateSetting("temperature", parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="enable_audit_logging"
              checked={settings.enable_audit_logging}
              onCheckedChange={(checked) => updateSetting("enable_audit_logging", checked)}
            />
            <Label htmlFor="enable_audit_logging">Habilitar registro de auditoría</Label>
          </div>

          <div className="space-y-2">
            <Label>Roles permitidos</Label>
            <div className="space-y-2">
              {["admin", "vendedor", "cliente"].map((role) => (
                <div key={role} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`ai_role_${role}`}
                    checked={settings.allowed_roles.includes(role)}
                    onChange={(e) => {
                      const nextRoles = e.target.checked
                        ? [...settings.allowed_roles, role]
                        : settings.allowed_roles.filter((item) => item !== role);
                      updateSetting("allowed_roles", nextRoles);
                    }}
                  />
                  <Label htmlFor={`ai_role_${role}`} className="capitalize">
                    {role}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Guardar Configuración"}
        </Button>
      </div>
    </form>
  );
}
